// biome-ignore-all lint/suspicious/noArrayIndexKey: flagged lists are documents/invoices/file slots, comment threads, and per-child item tables — all derived directly from request data on each render with a stable order. Items are never reordered or removed mid-render; the slot index IS the stable identity.
// biome-ignore-all lint/a11y/useSemanticElements: three div+role="button" patterns are intentional — each wraps child content that includes other interactive elements (HelpButton, nested category buttons), so a real <button> wrapper would create invalid nested-button HTML. Each flagged element has role + tabIndex + onKeyDown handling for keyboard a11y.
//
// =============================================================================
// RequestDetail.jsx
// =============================================================================
// The single-request view used by both shelter users (read-only, mostly) and
// staff/responders (full review + approval flow). Once the biggest file in the
// codebase (~8,500 lines at peak), it has been fully decomposed: this file now
// holds only the ReqDetail orchestrator (~600 lines), which wires state and
// hooks together and delegates all rendering to the sibling components below.
//
// COMPONENTS THIS FILE COMPOSES (all extracted to their own files)
// ────────────────────────────────────────────────────────────────
//   Read-only display atoms        → ./RequestDetailAtoms.jsx
//     SectionTitle / ROField / RODivider / ROSubtotal / ChildPanel / ApprovalBadge
//   Mid-level panels                → ./RequestDetailPanels.jsx
//     DetailSection / ShoesDetailPanel / StaffFilesPanel / ShelterInfoBox
//   Title bar + banners             → ./RequestTitleBar.jsx, ./RequestDetailBanners.jsx
//   Take-On lock + caseworker note  → ./TakeOnLock.jsx, ./CaseworkerNotesStrip.jsx
//   Shelter (submitted) view        → ./ShelterRequestView.jsx
//   Shelter draft fallback          → ./LeftPanel.jsx + ./Draft{Comments,Child,Files}*.jsx
//   Staff top info strip            → ./StaffTopInfoStrip.jsx
//   Staff approval surface          → ./ResponderChildView.jsx, ./ApprovalDecisions.jsx
//   Slide-in panels / modals        → ./CommentPanel.jsx, ./FilesPanel.jsx,
//                                      ./EditAddressModal.jsx, ./TemplatePickerModal.jsx
//
// ReqDetail itself is the only export.
//
// DATA FLOW
// ─────────
//   - Parent (portal.jsx) passes `req` (loaded from requests_view) and `user`,
//     plus onSave / onSaveNoDb / onContinueEdit / onBack / onDeleteDraft callbacks.
//   - Overall request status is owned here via the guarded requestLifecycle
//     reducer (P5.3 Slice 3.1): `deriveStatus(children)` is the single
//     client-side derivation, `setStatus` applies a loaded value. Children
//     derive through the injected `deriveStatus` rather than calling the reducer.
//   - The approval-confirmation flow (confirmAndSend) lives in ApprovalDecisions.jsx;
//     pure helpers it uses are in src/services/approvals.js.
//   - Realtime OSD codes: useOSDWatch subscribes to children_uniforms UPDATEs on
//     this request's child ids so an inbound School Locker code shows immediately.
//
// MAINTENANCE NOTE
// ────────────────
//   Decomposition is effectively complete — keep new pure logic in
//   src/services/approvals.js and new UI in a sibling component rather than
//   growing this orchestrator back out.
//
// SEE ALSO
// ────────
//   src/services/approvals.js              — pure helpers (deriveOverallStatus, applyApprovalStatuses, ...)
//   src/services/requestLifecycle.js       — the guarded status reducer (LOAD / SUBMIT / DERIVE)
//   src/components/form/RequestWizard.jsx  — TabFiles and TabGeneral reused here
//   src/components/form/ChildSections.jsx  — per-child sections reused inside ResponderChildView
// =============================================================================

import React, { useCallback, useReducer, useState } from "react";
import { isAdminRole, isInternalUser, isPickerJob, isResponderJob } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { useRepeatCheckSettings } from "../../app/UiConfigContext.jsx";
import { ADDRESS_PROMOTION_THRESHOLD, verifyAddressStep } from "../../data/deliveryAddresses";
import { getRequestNotesById, getRequestViewRowById, updateRequestById } from "../../data/requests";
import { countOpenResponderTasks, updateResponderTasksByRequest } from "../../data/requestTasks";
import { useCommentPanel } from "../../hooks/useCommentPanel";
import { useFileUploads } from "../../hooks/useFileUploads";
import { useNextStepsTemplates } from "../../hooks/useNextStepsTemplates";
import { useOSDWatch } from "../../hooks/useOSDWatch";
import { usePickerTaskLock } from "../../hooks/usePickerTaskLock";
import { loadFreshRepeats, useRepeatCheck } from "../../hooks/useRepeatCheck";
import { useSchoolLockerSubmission } from "../../hooks/useSchoolLockerSubmission";
import { useShelterAddresses } from "../../hooks/useShelterAddresses";
import { useTaskLock } from "../../hooks/useTaskLock";
import { NEXT_STEPS_DEFAULTS } from "../../services/approvals";
import { confirmDialog } from "../../services/dialogService";
import { confirmNavAway } from "../../services/navGuard";
import { requestLifecycleReducer } from "../../services/requestLifecycle";
import { logAudit } from "../../utils/auditLog";
import { REQUEST_STATUSES } from "../../utils/enums";
import { calcUniItems } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { dbToReq } from "../../utils/helpers";
import { ApprovalDecisions } from "./ApprovalDecisions.jsx";
import { AssignedElsewhere } from "./AssignedElsewhere.jsx";
import { CaseworkerNotesStrip } from "./CaseworkerNotesStrip.jsx";
import { CommentPanel } from "./CommentPanel.jsx";
import { DraftChildDisplay } from "./DraftChildDisplay.jsx";
import { EditAddressModal } from "./EditAddressModal.jsx";
import { FilesPanel } from "./FilesPanel.jsx";
import { LeftPanel } from "./LeftPanel.jsx";
import { PickerRequestView } from "./PickerRequestView.jsx";
import { PickerTakeOnGate } from "./PickerTakeOnGate.jsx";
import { RequestDetailBanners } from "./RequestDetailBanners.jsx";
import { RequestTitleBar } from "./RequestTitleBar.jsx";
import { ResponderChildView } from "./ResponderChildView.jsx";
import { ShelterRequestView } from "./ShelterRequestView.jsx";
import { StaffTopInfoStrip } from "./StaffTopInfoStrip.jsx";
import { TakeOnLock } from "./TakeOnLock.jsx";
import { TemplatePickerModal } from "./TemplatePickerModal.jsx";
import { TopUpPickerView } from "./TopUpPickerView.jsx";
import TopUpReadView from "./TopUpReadView.jsx";

// TemplatePickerModal moved to ./TemplatePickerModal.jsx — imported above.

// SectionTitle / ROField / RODivider / ROSubtotal / ChildPanel moved to
// ./RequestDetailAtoms.jsx — imported at the top of this file.

// DetailSection / ShoesDetailPanel moved to ./RequestDetailPanels.jsx — imported above.

// StaffFilesPanel moved to ./RequestDetailPanels.jsx — imported above.

// ShelterInfoBox moved to ./RequestDetailPanels.jsx — imported above.
// ─── APPROVAL DECISIONS ───────────────────────────────────────────────────────
// Shown to staff/admin only. Lets them set per-child-entry approval status
// across all categories. approvalStatus is stored inside each JSONB entry.
// No ALTER TABLE required — it's just an extra field in the existing JSONB arrays.

// ApprovalDecisions moved to ./ApprovalDecisions.jsx — imported above.

// ── RESPONDER CHILD VIEW ───────────────────────────────────────────────────────
// New staff view for new-schema requests: child tabs + category buttons + detail panel

// ─────────────────────────────────────────────────────────────────────────────
// ReqDetail — top-level orchestrator (the only EXPORT from this file)
// ─────────────────────────────────────────────────────────────────────────────
// Renders: the entire request-detail page. Branches on user.role:
//          - Shelter user → read-only sections + comments + inline delivery edit
//          - Staff/responder → ResponderChildView + ApprovalDecisions +
//            confirm-and-send flow
// Owns:
//   - Local approvals state (synced from req.children on id change)
//   - The osd-watch realtime subscription (refresh on inbound School Locker
//     codes; refs hold latest req/onSaveNoDb so the merge isn't stale —
//     see comments near reqRef/onSaveNoDbRef declarations).
//   - The confirmAndSend flow: stamps confirmed_at, persists, builds the
//     approval-summary email, auto-completes responder tasks, audits.
// Props (selected):
//   req, user                  - the request and current user
//   onSave(updated)            - persist a full request edit (DB + state)
//   onSaveNoDb(updated)        - update local state only (e.g. after realtime patch)
//   onContinueEdit, onClose,
//   onDeleteDraft, fromTodo    - parent navigation callbacks
// Notes: this function body is now ~600 lines and is mostly state/hook wiring
//        plus a JSX return that delegates to the sibling components listed in
//        the file header. Pure helpers live in services/approvals.js — please
//        continue that pattern when adding anything pure.
function ReqDetail({ req, onBack, onSave, onSaveNoDb, onContinueEdit, fromTodo, onDeleteDraft }) {
  const { user } = useSession();
  // onSave — a staff/admin edit with no prior DB write (updateRequest handles the write)
  // onSaveNoDb — used by all internal saves that already wrote to DB (approvals, files, status, comments, auth docs)
  const [_editMode, _setEditMode] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editDelivery, setEditDelivery] = useState("");
  // Overall request status is owned here through the guarded lifecycle reducer
  // (P5.3 Slice 3.1). deriveStatus is the single client-side computation of
  // overall status — it runs the children's per-item statuses through the
  // reducer's DERIVE and returns the value, so every caller derives through one
  // guarded path instead of calling the reducer itself. setStatus updates the
  // owner's status via the reducer's LOAD action (callers invoke it after they
  // persist, exactly as before).
  const [lifecycle, dispatchLifecycle] = useReducer(requestLifecycleReducer, {
    status: req.status,
  });
  const status = lifecycle.status;
  const deriveStatus = useCallback(
    function (children) {
      return requestLifecycleReducer(
        { status: req.status },
        {
          type: "DERIVE",
          children: children || [],
          // Terminal lock (D-2/D-4): a CONFIRMED Fully Processed request never
          // re-derives downward. `confirmed` is the persisted approvalsConfirmedAt
          // — NOT the bare status — so pre-confirm staging (which can reach Fully
          // Processed in-memory with approvalsConfirmedAt null) is not frozen.
          confirmed: req.approvalsConfirmedAt != null,
        }
      ).status;
    },
    [req.status, req.approvalsConfirmedAt]
  );
  const setStatus = useCallback(function (s) {
    dispatchLifecycle({ type: "LOAD", status: s });
  }, []);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(req.notes || []);
  // Tracks how many times the Comments-modal notification button has been
  // clicked for this request — drives "Send" vs "Re-send" label. Resets when
  // the user opens a different request.
  const [_notifyClickCount, setNotifyClickCount] = useState(0);
  React.useEffect(
    function () {
      setNotifyClickCount(0);
    },
    [req.id]
  );
  // Count embedded files from children (brochures, BYOD docs)
  let _embeddedFileCount = 0;
  (req.children || []).forEach(function (child) {
    (child.fees || []).forEach(function (fee) {
      if (fee?.brochure) _embeddedFileCount++;
    });
    // db-168: byod stores an object PATH now (not an http URL), so count any non-empty string.
    if (child.laptop?.byod && typeof child.laptop.byod === "string") _embeddedFileCount++;
  });
  // Shelter registered delivery addresses — extracted to hooks/useShelterAddresses.js
  const { shelterRegAddrs, shelterRegAddrObjs, shelterAddrsLoaded } = useShelterAddresses(
    req.id,
    req.shelterUuid,
    req.shelterName
  );

  useOSDWatch(req.id, req, onSaveNoDb);

  // Next-steps action sheet templates (editable via admin)
  const _nst = useNextStepsTemplates(NEXT_STEPS_DEFAULTS);
  const nextStepsTemplates = _nst.templates;

  const [sectionsOpen, setSectionsOpen] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [emailInvoicing, setEmailInvoicing] = useState(false);
  // Comment + Files slide-in panels — extracted to hooks/useCommentPanel.js
  const { showCommentPanel, setShowCommentPanel, showFilesPanel, setShowFilesPanel } =
    useCommentPanel();
  // Shelter-side selection state — used inside the "Child summary cards + detail"
  // section further down. Lifted out of the inline IIFE there to satisfy
  // useHookAtTopLevel (hooks must be called at the top of the component, not
  // from a nested function).
  const [shelterSel, setShelterSel] = useState(null);
  const [actionSheetOpen, setActionSheetOpen] = useState(null); // { ci, sessionDate }
  const _DOC_LABEL_OPTIONS = [
    "BYOD / Device Policy Letter",
    "School Fee Statement",
    "School Letter (fee waiver)",
    "Proof of Enrolment",
    "Camp Brochure",
    "Stationery List",
    "Invoice / Receipt",
    "Other",
  ];
  // School Locker submission — extracted to hooks/useSchoolLockerSubmission.js
  const { slSubmitState, slSubmitMsg, submitToSchoolLocker } = useSchoolLockerSubmission(req, user);
  // The picker view shows for a pure Picker (their only screen) and for any picker-job
  // user (Supervisor / Superuser / picker-mode Combined) who opened THIS request from the
  // The picker (processing) view is ONLY for a picker-job user who opened FROM the Pickers
  // List (fromTodo === "pickers"). Opening the same request from All Requests instead falls
  // through to the read-only ShelterRequestView below — read access via db-177 (27 Aug 2026).
  const isPicker = isPickerJob(user) && fromTodo === "pickers";
  // A picker-job user IN the picker view is not treated as staff (no approvals panel, etc.),
  // so the picker view renders identically whether a Picker or a Supervisor/Superuser opens it.
  const isStaff = isResponderJob(user) && !isPicker;
  // ── THE FENCE (John, 27 Aug 2026) — shelter vs Zephyr, mirroring the data wall
  // (docs/staff-caseworker-separation-plan.md). ONE primary gate: is this viewer a
  // shelter caseworker or an internal Zephyr user? isZephyr governs FRAMING + comment
  // identity (which side's conversation surface, who a comment is authored as, whether
  // the shelter's unread flag is touched); isStaff (above) governs ONLY the approve/edit
  // CONTROLS. They are mutually exclusive by the data wall. Because of this split a Zephyr
  // viewer — including a picker who opened a request from All Requests — never inherits the
  // shelter's comment surface, unread marker, or authorship, even though (having no approve
  // rights) they still render the read-only request body.
  const isZephyr = isInternalUser(user);
  const isShelterUser = !isZephyr;
  // Stock Top-ups (request_type) render their OWN read-only order view, not the
  // children-based fit-out views (which are blank for a top-up).
  const isTopUp = req.requestType === "stock_top_up";
  // Option (b): the picker works ONE SO at a time. The Pickers List opens the request
  // with ?picker_so=<order_number> (db-118); the picker view scopes its items, release
  // and completion to just that SO. null when opened without a task scope (fallback).
  const pickerScopeSo = isPicker
    ? new URLSearchParams(window.location.search).get("picker_so")
    : null;
  // A picker opening a stock-top-up SO from the Pickers List gets the dedicated top-up
  // picker card (not the read view, not the children-based PickerRequestView).
  const isTopUpPicker = isTopUp && isPicker && !!pickerScopeSo;
  // Possible-repeat flag (db-210): fees/devices that look like ones already requested for
  // the same child. Responder view only (isStaff), never for top-ups or DFV Service users.
  // Re-checked after a confirm (approvalsConfirmedAt changes). Best-effort: no flag on error.
  const repeatSettings = useRepeatCheckSettings();
  const repeatCheck = useRepeatCheck(req.id, {
    enabled: isStaff && !isTopUp,
    months: repeatSettings.months,
    types: repeatSettings.types,
    reloadKey: req.approvalsConfirmedAt,
  });

  // ── DFV Service "unread comment" nudge (db-77, Option B) ────────────────────
  // shelterUnread mirrors the request's shelter_has_unread_comment flag and drives
  // the reddy-orange Comments button in ShelterRequestView. It clears the instant
  // the shelter opens Comments (button click OR the ?comments=1 email deep-link),
  // writing the flag false so it stays cleared on the next load.
  const [shelterUnread, setShelterUnread] = useState(!!req.shelterHasUnreadComment);
  React.useEffect(
    function () {
      setShelterUnread(!!req.shelterHasUnreadComment);
    },
    [req.id, req.shelterHasUnreadComment]
  );
  const clearShelterUnread = useCallback(
    function () {
      setShelterUnread(false);
      if (req.id) updateRequestById(req.id, { shelter_has_unread_comment: false });
    },
    [req.id]
  );
  // Honour the ?comments=1 email deep-link: open the Comments panel on arrival,
  // and (for a shelter user) clear the unread flag since they're now reading it.
  React.useEffect(
    function () {
      let wantComments = false;
      try {
        wantComments = new URLSearchParams(window.location.search).get("comments") === "1";
      } catch {
        wantComments = false;
      }
      if (wantComments) {
        setShowCommentPanel(true);
        // Only an actual shelter user clears THEIR unread marker on arrival. A Zephyr
        // viewer (e.g. a picker from All Requests) must never touch the shelter's flag.
        if (isShelterUser) clearShelterUnread();
      }
    },
    // Run once on arrival (the email deep-link is a fresh app load at /requests/:id).
    []
  );

  // Exit-prompt tracking. actedThisSessionRef = the responder did work this visit
  // that does NOT auto-clear the task (added a comment / uploaded a file).
  // confirmedThisSessionRef = they ran Confirm & Send (which already clears the
  // tasks — so the exit prompt must not then re-offer). Drives handleBack's
  // "mark this task actioned on the way out?" prompt (see below).
  const actedThisSessionRef = React.useRef(false);
  const confirmedThisSessionRef = React.useRef(false);
  const markActed = useCallback(function () {
    actedThisSessionRef.current = true;
  }, []);

  // --- Take-On lock: staff must "Take On" a request before working on it ---
  // Extracted to hooks/useTaskLock.js
  const { taskLock, taskLockLoading, takingOn, handleTakeOn, assignedToOther } = useTaskLock(
    req.id,
    isStaff,
    user
  );
  // ── Task-ownership gate (UI only) ───────────────────────────────────────────
  // A task assigned to another same-role worker is off-limits to peers: a plain
  // Responder can't open another Responder's request; a plain Picker can't open a
  // Sales Order held by another Picker (SO-scoped, option (b)). A Supervisor+
  // (isAdminRole = admin/superuser) may open either and sees a view-only notice.
  const isSupervisor = isAdminRole(user);
  const pickerLock = usePickerTaskLock(req.id, pickerScopeSo, isPicker, user);
  // Responder peer blocked from opening a request held by another responder.
  const respBlockedByOther = isStaff && !!assignedToOther && !isSupervisor;
  // Picker gate states (loading / must-claim / blocked) that hide the request body.
  const pickerHidesContent =
    isPicker &&
    (pickerLock.loading ||
      pickerLock.needsAssign ||
      (!!pickerLock.assignedToOther && !isSupervisor));
  // The request body renders only when nothing gates it: not held by another
  // responder (staff), not locked/loading, and not behind a picker gate.
  const showRequestBody =
    (!isStaff || (!taskLock && !taskLockLoading && !respBlockedByOther)) && !pickerHidesContent;

  // Refresh notes from DB on mount to catch comments added in other sessions
  React.useEffect(
    function () {
      if (!req.id) return;
      getRequestNotesById(req.id).then(function (fresh) {
        if (fresh) setNotes(fresh);
      });
    },
    [req.id]
  );

  // Warn before the browser unloads (refresh / close / navigation / a forced
  // browser update) while there are unconfirmed approval decisions, so in-progress
  // work isn't abandoned without a prompt. Browsers show their own generic message;
  // setting returnValue is what triggers the native confirm dialog.
  React.useEffect(
    function () {
      function onBeforeUnload(e) {
        const hasStagedDecisions =
          isStaff &&
          req.approvalsConfirmedAt === null &&
          (status === REQUEST_STATUSES.PART_PROCESSED ||
            status === REQUEST_STATUSES.FULLY_PROCESSED);
        if (hasStagedDecisions) {
          e.preventDefault();
          e.returnValue = "";
          return "";
        }
      }
      window.addEventListener("beforeunload", onBeforeUnload);
      return function () {
        window.removeEventListener("beforeunload", onBeforeUnload);
      };
    },
    [isStaff, status, req.approvalsConfirmedAt]
  );

  function showSaved(msg, durationMs) {
    setSavedMsg(msg || `Saved ${GLYPH.check}`);
    setTimeout(function () {
      setSavedMsg("");
    }, durationMs || 3000);
  }

  // Verified-address model: a responder verifies the request's shelter-entered
  // one-off delivery address inline (self-unblocks the Device / Stationery-Pack
  // approval hold). RLS "Staff manage all addresses" already permits staff/admin/
  // superuser to write zephyr_verified. Updates the in-memory req so the amber
  // warning + the approval block clear immediately (no reload).
  async function verifyDeliveryAddress() {
    if (!req.deliveryAddressId) return;
    // Promotion model (db-146): a verify bumps the address's use_count toward 3 and
    // only flips the address globally trusted (zephyr_verified) once it reaches 3.
    // THIS request always clears immediately in-memory (below) so the responder can
    // proceed now; the address itself stays "needs verifying" on future requests
    // until it's been verified 3× (then it shows on the shelter's pick-list).
    const res = await verifyAddressStep(req.deliveryAddressId);
    if (!res) {
      showSaved(`Verify failed ${GLYPH.cross}`);
      return;
    }
    logAudit("delivery_address_verified", {
      id: req.deliveryAddressId,
      request_id: req.id,
      ref_no: req.refNo,
      use_count: res.use_count,
      promoted: res.zephyr_verified,
      by: user?.email || "",
    });
    onSaveNoDb(Object.assign({}, req, { deliveryAddressVerified: true }));
    showSaved(
      res.zephyr_verified
        ? `Address verified & added to the list ${GLYPH.check}`
        : `Address verified (${res.use_count} of ${ADDRESS_PROMOTION_THRESHOLD}) ${GLYPH.check}`
    );
  }

  // File uploads (docs + invoices) — extracted to hooks/useFileUploads.js
  const { docs, setDocs, invoices, setInvoices, fileUploading, uploadFile } = useFileUploads({
    req,
    user,
    isStaff,
    onSaveNoDb,
    showSaved,
    setNotes,
  });
  // Wrap uploadFile so a completed upload counts as responder "activity" for the
  // exit prompt (a file upload, like a comment, does not auto-clear the task).
  const uploadFileTracked = useCallback(
    async function (...args) {
      const res = await uploadFile(...args);
      markActed();
      return res;
    },
    [uploadFile, markActed]
  );

  // Calculate totals from children[]
  const childFeeTotal = (req.children || []).reduce(function (s, c) {
    return (
      s +
      (c.fees || []).reduce(function (fs, f) {
        return fs + (parseFloat(f.feeAmount) || 0);
      }, 0)
    );
  }, 0);
  const childUniTotal = (req.children || []).reduce(function (s, c) {
    return s + (c.uniforms ? calcUniItems([c.uniforms]) : 0);
  }, 0);
  const childShoeTotal = (req.children || []).reduce(function (s, c) {
    return (
      s +
      (c.shoes
        ? (c.shoes.jQty || 0) * (parseFloat(c.shoes.jCost) || 0) +
          (c.shoes.lQty || 0) * (parseFloat(c.shoes.lCost) || 0)
        : 0)
    );
  }, 0);
  const childLapTotal = (req.children || []).reduce(function (s, c) {
    return s + (c.laptop ? parseFloat(c.laptop.estimatedCost) || 0 : 0);
  }, 0);
  const childHasLaptop = (req.children || []).some(function (c) {
    return !!c.laptop;
  });

  const feeTotal = childFeeTotal;
  const slTotal = 0;
  const uniTotal = childUniTotal;
  const shoeTotal = childShoeTotal;
  const laptopTotal = childLapTotal;
  const grand = feeTotal + slTotal + uniTotal + shoeTotal + laptopTotal;
  const hasLaptops = childHasLaptop;
  const totalChildren = (req.children || []).length;

  // Guarded back handler — WARNS if decisions are staged but not confirmed, but
  // never traps the user. Uses the LIVE derived `status` (not the `req` prop,
  // which can lag a just-saved revert) so the warning reflects the real state.
  async function handleBack() {
    // Respect any registered unsaved-changes guard (e.g. dirty Shelter Notes /
    // the no-approvals flag in ShelterInfoBox) before leaving.
    if (!(await confirmNavAway())) return;
    const hasStagedDecisions =
      isStaff &&
      req.approvalsConfirmedAt === null &&
      (status === REQUEST_STATUSES.PART_PROCESSED || status === REQUEST_STATUSES.FULLY_PROCESSED);
    if (hasStagedDecisions) {
      // Warn, but always offer a way out: leaving DISCARDS the unconfirmed
      // decisions (staging is in-memory only - nothing is saved until Confirm & Send);
      // Stay returns to the request. A hard block previously left the browser back-button as the only
      // exit, which drops the user out of the app entirely.
      const leave = await confirmDialog({
        title: "Leave and lose these decisions?",
        message:
          "This request has decisions that have NOT been confirmed and sent to the DFV Service.\n\n" +
          "If you leave now, these decisions will be LOST. Nothing is saved until you click Confirm & Send to DFV Service — there is no staged copy kept for you or anyone else to come back to.\n\n" +
          "Confirm & Send first to keep them, or leave and discard.",
        confirmLabel: "Leave & discard",
        cancelLabel: "Stay",
      });
      if (!leave) return;
      // Leaving DISCARDS the in-memory staging: re-sync this request from the DB so the
      // list (and any later re-open) shows the persisted truth, never a phantom "staged".
      const freshRow = await getRequestViewRowById(req.id);
      if (freshRow) onSaveNoDb(dbToReq(freshRow));
    } else if (
      isStaff &&
      fromTodo === "todo" &&
      actedThisSessionRef.current &&
      !confirmedThisSessionRef.current
    ) {
      // The responder took on a task from the queue and did work that does NOT
      // auto-clear it (a comment / file upload — only Confirm & Send auto-clears),
      // and is now leaving without confirming. Offer to mark the request's open
      // responder task(s) actioned so it leaves the queue. Skipped entirely when
      // they confirmed (already cleared), when they only read the request, or when
      // nothing is still open.
      // Assignee-only: count only OPEN responder tasks assigned to me — Actioned
      // (and this prompt) applies solely to the responder the task is assigned to.
      const openCount = await countOpenResponderTasks(req.id, user.id);
      if (openCount > 0) {
        const markDone = await confirmDialog({
          title: "Mark this task as actioned?",
          message:
            "You added a comment or file but haven't sent an approval decision.\n\n" +
            `Would you like to mark this request's responder task${openCount !== 1 ? "s" : ""} ` +
            "as actioned so it leaves the queue? Leave it open if you're not finished.",
          confirmLabel: "Mark actioned",
          cancelLabel: "Leave open",
        });
        if (markDone) {
          await updateResponderTasksByRequest(
            req.id,
            { completed_at: new Date().toISOString() },
            "open"
          );
          logAudit(
            "task_actioned",
            { request_id: req.id, ref: req.refNo, by: user.shelterName },
            req.id
          );
        }
      }
    }
    onBack();
  }

  return (
    <div style={{ padding: "20px 28px", maxWidth: "var(--page-max)", margin: "0 auto" }}>
      <button
        onClick={handleBack}
        style={{
          background: "none",
          border: "none",
          color: "#00897B",
          fontSize: 14,
          cursor: "pointer",
          marginBottom: 16,
          fontFamily: "inherit",
          padding: 0,
          fontWeight: 600,
        }}
      >
        {fromTodo === "pickers"
          ? `${GLYPH.arrowLeft} Return to Pickers Task List`
          : fromTodo === "todo"
            ? `${GLYPH.arrowLeft} Return to Responders Task List`
            : `${GLYPH.arrowLeft} Back to list`}
      </button>

      {/* Take-On lock — extracted to ./TakeOnLock.jsx (owns its own staff/loading/locked gating) */}
      <TakeOnLock
        isStaff={isStaff}
        taskLock={taskLock}
        taskLockLoading={taskLockLoading}
        takingOn={takingOn}
        handleTakeOn={handleTakeOn}
        req={req}
      />

      {/* Responder ownership gate: a request whose active task is held by another
          responder is off-limits to peers (blocking card); a supervisor+ may open
          it and sees a slim notice while the content still renders below. */}
      {isStaff && !!assignedToOther && !taskLockLoading && (
        <AssignedElsewhere req={req} name={assignedToOther} blocked={!isSupervisor} />
      )}

      {/* Picker take-on / ownership gate (SO-scoped, option (b)). */}
      {isPicker && pickerLock.loading && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#607D79", fontSize: 14 }}>
          Loading...
        </div>
      )}
      {isPicker && !pickerLock.loading && pickerLock.needsAssign && (
        <PickerTakeOnGate
          req={req}
          soNumber={pickerScopeSo}
          takingOn={pickerLock.takingOn}
          onAssign={pickerLock.handleAssign}
        />
      )}
      {isPicker && !pickerLock.loading && !!pickerLock.assignedToOther && !isSupervisor && (
        <AssignedElsewhere req={req} name={pickerLock.assignedToOther} blocked />
      )}

      {/* All request content — hidden when locked or blocked by another's ownership */}
      {showRequestBody && (
        <React.Fragment>
          {/* Title bar — extracted to ./RequestTitleBar.jsx */}
          <RequestTitleBar
            req={req}
            status={status}
            isZephyr={isZephyr}
            notes={notes}
            docs={docs}
            invoices={invoices}
            setShowCommentPanel={setShowCommentPanel}
            setShowFilesPanel={setShowFilesPanel}
            onSave={onSave}
            showSaved={showSaved}
            onContinueEdit={onContinueEdit}
            totalChildren={totalChildren}
          />

          {/* Save toast + staged-decisions + draft banners — extracted to ./RequestDetailBanners.jsx */}
          <RequestDetailBanners
            savedMsg={savedMsg}
            isStaff={isStaff}
            req={req}
            onContinueEdit={onContinueEdit}
            onDeleteDraft={onDeleteDraft}
            onBack={onBack}
          />

          {/* Stock Top-up: read-only order view — EXCEPT when a picker opened a scoped SO
              (they get the editable TopUpPickerView below instead). The fit-out children
              views below are blank for a top-up, gated off with !isTopUp / isTopUp?null. */}
          {isTopUp && !isTopUpPicker && (
            <TopUpReadView
              req={req}
              isShelterUser={isShelterUser}
              notes={notes}
              setShowCommentPanel={setShowCommentPanel}
              hasUnread={shelterUnread}
              onCommentsOpened={clearShelterUnread}
            />
          )}

          {/* ── SHELTER VIEW — mirrors responder layout. NOT for pickers: a picker has
              isStaff=false but must see the picker view below, not the shelter read view
              (otherwise both render — the duplicate top block John saw, 26 Jul 2026). ── */}
          {!isStaff &&
            !isPicker &&
            !isTopUp &&
            !(req.status === REQUEST_STATUSES.DRAFT || req.refNo?.startsWith("DRAFT")) && (
              <ShelterRequestView
                req={req}
                notes={notes}
                user={user}
                isShelterUser={isShelterUser}
                shelterSel={shelterSel}
                setShelterSel={setShelterSel}
                actionSheetOpen={actionSheetOpen}
                setActionSheetOpen={setActionSheetOpen}
                setShowCommentPanel={setShowCommentPanel}
                hasUnread={shelterUnread}
                onCommentsOpened={clearShelterUnread}
                feeTotal={feeTotal}
                grand={grand}
                childUniTotal={childUniTotal}
                childShoeTotal={childShoeTotal}
                childLapTotal={childLapTotal}
                nextStepsTemplates={nextStepsTemplates}
              />
            )}

          {/* ── PICKER VIEW — its own dedicated component. RequestDetail routes here for
              a picker-job user who opened from the Pickers List; NO responder-view
              conditionals (the old strip-down approach is retired). ── */}
          {isTopUpPicker ? (
            <TopUpPickerView
              req={req}
              user={user}
              onDone={onBack}
              scopeSoNumber={pickerScopeSo}
              assignedToOther={isSupervisor ? pickerLock.assignedToOther : null}
            />
          ) : isTopUp ? null : isPicker ? (
            <PickerRequestView
              req={req}
              user={user}
              onDone={onBack}
              scopeSoNumber={pickerScopeSo}
              assignedToOther={isSupervisor ? pickerLock.assignedToOther : null}
            />
          ) : isStaff ? (
            <div>
              {/* Top info strip — extracted to ./StaffTopInfoStrip.jsx */}
              <StaffTopInfoStrip
                req={req}
                shelterRegAddrs={shelterRegAddrs}
                shelterAddrsLoaded={shelterAddrsLoaded}
                setEditDelivery={setEditDelivery}
                setShowAddressModal={setShowAddressModal}
                onVerifyAddress={isStaff ? verifyDeliveryAddress : null}
                totals={{ feeTotal, slTotal, uniTotal, shoeTotal, laptopTotal, hasLaptops, grand }}
                repeatSummary={repeatCheck.summary}
              />

              {/* Shelter caseworker notes (form-submission note only) — kept for pickers
                  too: shelters sometimes note here why a different delivery address is used. */}
              <CaseworkerNotesStrip req={req} />

              {/* Child tab view */}

              <ResponderChildView
                req={req}
                onSave={onSave}
                onSaveNoDb={onSaveNoDb}
                showSaved={showSaved}
                notes={notes}
                setNotes={setNotes}
                setShowCommentPanel={setShowCommentPanel}
                setNote={setNote}
                setStatus={setStatus}
                deriveStatus={deriveStatus}
                openCommentWithTemplate={function (draftText, openOnly) {
                  if (!openOnly && draftText) {
                    setNote(function (existing) {
                      return existing.trim() ? `${existing}\n\n---\n\n${draftText}` : draftText;
                    });
                  }
                  setShowCommentPanel(true);
                }}
                invoices={invoices}
                setInvoices={setInvoices}
                docs={docs}
                setDocs={setDocs}
                uploadFile={uploadFileTracked}
                fileUploading={fileUploading}
                slSubmitState={slSubmitState}
                slSubmitMsg={slSubmitMsg}
                actionSheetOpen={actionSheetOpen}
                setActionSheetOpen={setActionSheetOpen}
                nextStepsTemplates={nextStepsTemplates}
                repeats={repeatCheck.byItem}
                repeatReview={repeatCheck.review}
                repeatMonths={repeatSettings.months}
              />

              {isStaff && !isPicker && (
                <ApprovalDecisions
                  req={req}
                  onSave={function (savedReq) {
                    onSaveNoDb(savedReq);
                    setStatus(savedReq.status || status);
                    if (savedReq.notes) setNotes(savedReq.notes);
                  }}
                  deriveStatus={deriveStatus}
                  showSaved={showSaved}
                  openCommentWithTemplate={function (draftText, openOnly) {
                    if (!openOnly && draftText) {
                      setNote(function (existing) {
                        return existing.trim() ? `${existing}\n\n---\n\n${draftText}` : draftText;
                      });
                    }
                    setShowCommentPanel(true);
                  }}
                  onSchoolLockerApproved={submitToSchoolLocker}
                  onConfirmed={function () {
                    confirmedThisSessionRef.current = true;
                  }}
                  repeats={repeatCheck.byItem}
                  refreshRepeats={
                    isStaff && !isTopUp
                      ? function () {
                          return loadFreshRepeats(req.id, repeatSettings);
                        }
                      : null
                  }
                />
              )}

              {/* Focused address-only edit modal — extracted to ./EditAddressModal.jsx */}
              {showAddressModal && (
                <EditAddressModal
                  editDelivery={editDelivery}
                  setEditDelivery={setEditDelivery}
                  shelterRegAddrs={shelterRegAddrs}
                  shelterRegAddrObjs={shelterRegAddrObjs}
                  user={user}
                  req={req}
                  onSaveNoDb={onSaveNoDb}
                  showSaved={showSaved}
                  onClose={function () {
                    setShowAddressModal(false);
                  }}
                />
              )}
            </div>
          ) : req.status === REQUEST_STATUSES.DRAFT || req.refNo?.startsWith("DRAFT") ? (
            // Fallback layout used for shelter users viewing a Draft. The submitted
            // shelter view above (search "SHELTER VIEW") already covers non-drafts,
            // so we no longer render this fallback for them — it caused a duplicate
            // detail block to appear beneath the shelter summary.
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "290px 1fr",
                gap: 20,
                alignItems: "start",
              }}
            >
              {/* LEFT PANEL — extracted to ./LeftPanel.jsx */}
              <LeftPanel
                req={req}
                user={user}
                isStaff={isStaff}
                isPicker={isPicker}
                status={status}
                setStatus={setStatus}
                deriveStatus={deriveStatus}
                setNotes={setNotes}
                setNote={setNote}
                setShowCommentPanel={setShowCommentPanel}
                showSaved={showSaved}
                onSaveNoDb={onSaveNoDb}
                submitToSchoolLocker={submitToSchoolLocker}
                totals={{ feeTotal, slTotal, uniTotal, shoeTotal, laptopTotal, hasLaptops, grand }}
              />

              {/* RIGHT PANEL */}
              <div>
                {/* Comments are intentionally not shown on a draft — they only
                    apply to a submitted request (see RequestTitleBar). */}

                {/* Comment modal and template picker now rendered outside the ternary. */}

                {/* Child-by-child display — extracted to ./DraftChildDisplay.jsx */}
                <DraftChildDisplay
                  req={req}
                  isStaff={isStaff}
                  sectionsOpen={sectionsOpen}
                  setSectionsOpen={setSectionsOpen}
                />

                {/* Files are added/managed in the wizard during creation (Continue
                    Editing). A draft intentionally shows no separate Files section
                    here — it duplicated the wizard's upload step. */}
              </div>
            </div>
          ) : null}

          {/* COMMENT MODAL — outside ternary, works for both layouts */}
          {showCommentPanel && (
            <CommentPanel
              req={req}
              user={user}
              isZephyr={isZephyr}
              notes={notes}
              setNotes={setNotes}
              note={note}
              setNote={setNote}
              emailInvoicing={emailInvoicing}
              setEmailInvoicing={setEmailInvoicing}
              onClose={function () {
                setShowCommentPanel(false);
                setNote("");
                setEmailInvoicing(false);
              }}
              showSaved={showSaved}
              setShowTemplatePicker={setShowTemplatePicker}
              onActivity={markActed}
            />
          )}
          {showTemplatePicker && (
            <TemplatePickerModal
              onSelect={function (text) {
                setNote(function (prev) {
                  return prev ? `${prev}\n\n${text}` : text;
                });
              }}
              onClose={function () {
                setShowTemplatePicker(false);
              }}
            />
          )}

          {/* ── Files & Invoices slide-in panel (new-schema staff view) ── */}
          {showFilesPanel && (
            <FilesPanel
              req={req}
              docs={docs}
              setDocs={setDocs}
              invoices={invoices}
              setInvoices={setInvoices}
              fileUploading={fileUploading}
              uploadFile={uploadFileTracked}
              onClose={function () {
                setShowFilesPanel(false);
              }}
            />
          )}
        </React.Fragment>
      )}
    </div>
  );
}

export { ReqDetail };
